import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  TextField,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { WizardDialog } from '../WizardDialog';
import { WizardStep } from '../WizardStep';
import { useWizard } from '../../contexts/WizardContext';
import { ProgressSummaryPanel, SummarySection } from '../wizard-panels/ProgressSummaryPanel';
import { rangeosApiService } from '../../services/rangeos-api';
import { rangeosEnvironmentService, RangeOSEnvironment } from '../../services/rangeos-environment-api';

export interface TransferDraftWizardProps {
  open: boolean;
  onClose: () => void;
  onComplete?: (jobId: string) => void;
}

const wizardSteps = [
  { label: 'Select Source', description: 'Choose source RangeOS and drafts' },
  { label: 'Select Destination', description: 'Choose target RangeOS' },
  { label: 'Configure Transfer', description: 'Set transfer options' },
  { label: 'Review & Transfer', description: 'Review and transfer' }
];

// Step 1: Select Source RangeOS and Drafts
const SourceSelectionStep: React.FC = () => {
  const wizard = useWizard();
  const [environments, setEnvironments] = useState<RangeOSEnvironment[]>([]);
  const [drafts, setDrafts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [selectedDrafts, setSelectedDrafts] = useState<string[]>([]);

  useEffect(() => {
    const loadEnvironments = async () => {
      try {
        const rangeosEnvs = await rangeosEnvironmentService.getActiveEnvironments();
        setEnvironments(rangeosEnvs);
      } catch (error) {
        console.error('Failed to load RangeOS environments:', error);
      } finally {
        setLoading(false);
      }
    };

    loadEnvironments();
  }, []);

  useEffect(() => {
    const loadDrafts = async () => {
      const sourceEndpoint = wizard.getData('sourceEndpoint');
      if (!sourceEndpoint) return;

      setLoadingDrafts(true);
      try {
        const draftsData = await rangeosApiService.listDesignDrafts(sourceEndpoint);
        setDrafts(draftsData);
      } catch (error) {
        console.error('Failed to load drafts:', error);
      } finally {
        setLoadingDrafts(false);
      }
    };

    loadDrafts();
  }, [wizard.getData('sourceEndpoint')]);

  // Initialize selected drafts from wizard data
  useEffect(() => {
    const saved = wizard.getData('selectedDrafts', []);
    setSelectedDrafts(saved);
  }, []);

  // Update validation whenever selection changes
  useEffect(() => {
    const sourceEndpoint = wizard.getData('sourceEndpoint');
    const isValid = Boolean(sourceEndpoint && selectedDrafts.length > 0);
    wizard.setStepValidation(0, isValid);
  }, [selectedDrafts, wizard.getData('sourceEndpoint')]);

  const handleDraftToggle = (draftId: string) => {
    const newSelection = selectedDrafts.includes(draftId)
      ? selectedDrafts.filter(id => id !== draftId)
      : [...selectedDrafts, draftId];

    setSelectedDrafts(newSelection);
    wizard.setData('selectedDrafts', newSelection);

    // Update draft names for display
    const names = newSelection
      .map(id => drafts.find(d => d.id === id)?.name)
      .filter(Boolean);
    wizard.setData('selectedDraftNames', names);
  };

  const handleSelectAll = () => {
    if (selectedDrafts.length === drafts.length) {
      setSelectedDrafts([]);
      wizard.setData('selectedDrafts', []);
      wizard.setData('selectedDraftNames', []);
    } else {
      const allIds = drafts.map(d => d.id);
      setSelectedDrafts(allIds);
      wizard.setData('selectedDrafts', allIds);
      wizard.setData('selectedDraftNames', drafts.map(d => d.name));
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select Source RangeOS Instance and Drafts
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Choose the source RangeOS instance and the draft scenarios you want to transfer.
      </Typography>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Source RangeOS Environment</InputLabel>
        <Select
          value={wizard.getData('sourceEndpoint', '')}
          onChange={(e) => {
            wizard.setData('sourceEndpoint', e.target.value);
            setSelectedDrafts([]);
            wizard.setData('selectedDrafts', []);
          }}
          label="Source RangeOS Environment"
        >
          {environments.map((env) => (
            <MenuItem key={env.id} value={env.id}>
              {env.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {wizard.getData('sourceEndpoint') && (
        <Box>
          {loadingDrafts ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading drafts...</Typography>
            </Box>
          ) : drafts.length === 0 ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No drafts found in this RangeOS instance.
            </Alert>
          ) : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2">
                  Select Draft Scenarios ({selectedDrafts.length} of {drafts.length} selected)
                </Typography>
                <Chip
                  label={selectedDrafts.length === drafts.length ? "Deselect All" : "Select All"}
                  onClick={handleSelectAll}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
              <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto', mb: 2 }}>
                <List dense>
                  {drafts.map((draft) => (
                    <ListItem
                      key={draft.id}
                      button
                      onClick={() => handleDraftToggle(draft.id)}
                      sx={{
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '&:last-child': { borderBottom: 'none' }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <input
                          type="checkbox"
                          checked={selectedDrafts.includes(draft.id)}
                          onChange={() => handleDraftToggle(draft.id)}
                          style={{ marginRight: 12 }}
                        />
                        <ListItemText
                          primary={draft.name}
                          secondary={draft.description || 'No description'}
                        />
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </>
          )}
        </Box>
      )}

      {selectedDrafts.length > 0 && (
        <Alert severity="info">
          Ready to transfer <strong>{selectedDrafts.length}</strong> draft{selectedDrafts.length > 1 ? 's' : ''}
        </Alert>
      )}
    </Box>
  );
};

// Step 2: Select Destination RangeOS
const DestinationSelectionStep: React.FC = () => {
  const wizard = useWizard();
  const [environments, setEnvironments] = useState<RangeOSEnvironment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadEnvironments = async () => {
      try {
        const rangeosEnvs = await rangeosEnvironmentService.getActiveEnvironments();
        setEnvironments(rangeosEnvs);
      } catch (error) {
        console.error('Failed to load RangeOS environments:', error);
      } finally {
        setLoading(false);
      }
    };

    loadEnvironments();
  }, []);

  // Update validation whenever target endpoint changes
  useEffect(() => {
    const targetEndpoint = wizard.getData('targetEndpoint');
    wizard.setStepValidation(1, Boolean(targetEndpoint));
  }, [wizard.getData('targetEndpoint')]);

  if (loading) {
    return <CircularProgress />;
  }

  // Filter out the source environment from destination options
  const sourceEndpoint = wizard.getData('sourceEndpoint');
  const availableEnvironments = environments.filter(env => env.id !== sourceEndpoint);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select Destination RangeOS Environment
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Choose the RangeOS environment where the drafts will be transferred.
      </Typography>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Target RangeOS Environment</InputLabel>
        <Select
          value={wizard.getData('targetEndpoint', '')}
          onChange={(e) => wizard.setData('targetEndpoint', e.target.value)}
          label="Target RangeOS Environment"
        >
          {availableEnvironments.map((env) => (
            <MenuItem key={env.id} value={env.id}>
              {env.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {availableEnvironments.length === 0 && (
        <Alert severity="warning">
          No other RangeOS environments available. You need at least two configured RangeOS environments to transfer drafts.
        </Alert>
      )}

      {wizard.getData('targetEndpoint') && (
        <Alert severity="info">
          The drafts will be copied to the target environment as new design drafts.
        </Alert>
      )}
    </Box>
  );
};

// Step 3: Configure Transfer Options
const ConfigureTransferStep: React.FC = () => {
  const wizard = useWizard();

  // Step 2 is always valid (optional configuration)
  useEffect(() => {
    wizard.setStepValidation(2, true);
  }, []);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Configure Transfer Options
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Configure how the drafts should be transferred.
      </Typography>

      <FormControlLabel
        control={
          <Checkbox
            checked={wizard.getData('addTransferredPrefix', false)}
            onChange={(e) => {
              wizard.setData('addTransferredPrefix', e.target.checked);
              if (e.target.checked && !wizard.getData('transferPrefix')) {
                wizard.setData('transferPrefix', '(Transferred) ');
              } else if (!e.target.checked) {
                wizard.setData('transferPrefix', '');
              }
            }}
          />
        }
        label='Add "(Transferred)" prefix to draft names'
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Custom Draft Name Prefix (optional)"
        value={wizard.getData('transferPrefix', '')}
        onChange={(e) => {
          wizard.setData('transferPrefix', e.target.value);
          wizard.setData('addTransferredPrefix', false);
        }}
        placeholder="e.g., (Copied), Testing-, etc."
        helperText="Custom prefix to add to all transferred draft names (overrides the checkbox above)"
        sx={{ mb: 3 }}
      />

      <Alert severity="warning" sx={{ mb: 2 }}>
        <strong>Important:</strong> The transferred drafts will include all resource references (VMImages, Charts, etc.) from the source.
        These references may not exist in the target RangeOS instance and may need validation and remapping.
      </Alert>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          What will be transferred:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="Draft metadata (name, description, version)" />
          </ListItem>
          <ListItem>
            <ListItemText primary="All draft states and payloads" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Resource references (may need remapping)" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Scenario designer data" />
          </ListItem>
        </List>
      </Paper>

      <Alert severity="info" sx={{ mt: 2 }}>
        <strong>Tip:</strong> After transferring, use the Draft Validator to check and remap any invalid resource references.
      </Alert>
    </Box>
  );
};

// Step 4: Review and Confirm
const ReviewStep: React.FC = () => {
  const wizard = useWizard();
  const selectedDraftNames = wizard.getData('selectedDraftNames', []);
  const selectedDrafts = wizard.getData('selectedDrafts', []);

  // Review step is always valid
  useEffect(() => {
    wizard.setStepValidation(3, true);
  }, []);

  const summarySections: SummarySection[] = [
    {
      title: 'Source',
      items: [
        { label: 'RangeOS Instance', value: wizard.getData('sourceEndpoint') },
        { label: 'Drafts to Transfer', value: `${selectedDrafts.length} draft${selectedDrafts.length > 1 ? 's' : ''}` }
      ]
    },
    {
      title: 'Destination',
      items: [
        { label: 'RangeOS Instance', value: wizard.getData('targetEndpoint') }
      ]
    },
    {
      title: 'Transfer Options',
      items: [
        { label: 'Name Prefix', value: wizard.getData('transferPrefix') || 'None' }
      ]
    }
  ];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Review Transfer Configuration
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Please review the transfer configuration before proceeding.
      </Typography>

      <ProgressSummaryPanel sections={summarySections} />

      {selectedDraftNames.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Selected Drafts:
          </Typography>
          <List dense>
            {selectedDraftNames.map((name: string, idx: number) => (
              <ListItem key={idx}>
                <ListItemText primary={`${idx + 1}. ${name}`} />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      <Alert severity="warning" sx={{ mt: 2 }}>
        <strong>Note:</strong> The original drafts in the source RangeOS instance will not be modified.
        New copies will be created in the target instance with all their resource references.
        Use the Draft Validator to check and remap these references before publishing.
      </Alert>
    </Box>
  );
};

export const TransferDraftWizard: React.FC<TransferDraftWizardProps> = ({
  open,
  onClose,
  onComplete
}) => {
  const handleComplete = async (wizardData: Record<string, any>) => {
    const selectedDrafts = wizardData.selectedDrafts || [];
    const results = [];

    try {
      // Transfer each draft
      for (const draftId of selectedDrafts) {
        try {
          const response = await rangeosApiService.transferDraft({
            sourceEndpointId: wizardData.sourceEndpoint,
            sourceDraftId: draftId,
            targetEndpointId: wizardData.targetEndpoint,
            draftName: wizardData.transferPrefix ? `${wizardData.transferPrefix}${draftId}` : undefined
          });

          if (response.success) {
            console.log(`Draft ${draftId} transferred successfully:`, response);
            results.push({ draftId, success: true, newDraftId: response.draftId });
          } else {
            results.push({ draftId, success: false, error: response.message });
          }
        } catch (error: any) {
          console.error(`Failed to transfer draft ${draftId}:`, error);
          results.push({ draftId, success: false, error: error.message });
        }
      }

      const successCount = results.filter(r => r.success).length;
      console.log(`Transferred ${successCount}/${results.length} drafts successfully`);

      if (onComplete && successCount > 0) {
        const firstSuccess = results.find(r => r.success);
        onComplete(firstSuccess?.newDraftId || 'batch-transfer-complete');
      }

      if (successCount < results.length) {
        const failures = results.filter(r => !r.success);
        throw new Error(`${failures.length} draft(s) failed to transfer`);
      }
    } catch (error: any) {
      console.error('Transfer process encountered errors:', error);
      throw error;
    }
  };

  return (
    <WizardDialog
      open={open}
      onClose={onClose}
      title="Transfer Draft Scenarios Between RangeOS Instances"
      steps={wizardSteps}
      onComplete={handleComplete}
    >
      <WizardStep stepIndex={0}>
        <SourceSelectionStep />
      </WizardStep>
      <WizardStep stepIndex={1}>
        <DestinationSelectionStep />
      </WizardStep>
      <WizardStep stepIndex={2}>
        <ConfigureTransferStep />
      </WizardStep>
      <WizardStep stepIndex={3}>
        <ReviewStep />
      </WizardStep>
    </WizardDialog>
  );
};
